import { useState, useRef, useEffect } from 'react';
import cx from 'classnames';
import { Tooltip } from 'react-tooltip';
import { useDrag, useDrop } from 'react-dnd';
import { isMobile, isTablet } from 'react-device-detect';
import { toast } from 'react-toastify';
import { isResponseOk } from '../../../../../../utility/isResponseOk';
import { moveAssignmentToAnotherCellService } from '../../../../../../services/scheduling';
import OvertimeMark from './OvertimeMark';

const SecondHalfCell = ({
  cell,
  getPeriodData,
  cellAssignmentData,
  showRecurringShifts,
  isPrint,
  hasWritePermission,
  setCellAssignmentData,
  unit,
  role,
  shiftsData,
  isPastPeriod,
  isSelectingPostShiftsActive,
  setSelectedShiftsToPost,
  name,
  periodData,
  editSidebarAction,
  editSidebarFormData,
  editSidebarSelectedItems,
  setEditSidebarSelectedItems,
  editSidebarSelectedUser,
  selectedShiftsToPost,
}) => {
  const [collected, drag] = useDrag(
    () => ({
      type: 'scheduling_one_half_assignment',
      item: { assignment: { ...cell.second_half_user } },
      canDrag: !isMobile && !isTablet && hasWritePermission && !isPrint && !isSelectingPostShiftsActive && !editSidebarAction,
      end: async (item, monitor) => {
        try {
          const dropResult = monitor.getDropResult();
          if (dropResult?.cell) {
            if (!item.assignment.user) {
              toast.error("Custom users can't be moved between cells", { hideProgressBar: false });
              return;
            }

            const payload = {
              assignment_id: item.assignment.id,
              new_cell_id: dropResult.cell.id,
              half_part: dropResult.halfPart,
            };
            const response = await moveAssignmentToAnotherCellService(payload);
            if (isResponseOk(response)) {
              getPeriodData(true, false, true);
            }
          }
        } catch (e) {
          console.log(e);
        }
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [cell.second_half_user],
  );

  const [, drop] = useDrop(
    () => ({
      accept: ['scheduling_one_half_assignment'],
      canDrop: () => hasWritePermission && !cell.second_half_user,
      drop: () => ({ cell, halfPart: 'second_half' }),
    }),
    [cell.second_half_user, hasWritePermission],
  );

  const handleCellClick = () => {
    if (editSidebarAction) {
      if (
        editSidebarAction === 'add' &&
        !cell.second_half_user &&
        ((editSidebarSelectedUser.is_nurse && role.role === 'nurse') || (editSidebarSelectedUser.is_cna && role.role === 'cna')) &&
        editSidebarFormData.halfPart.value === 'second_half' &&
        !cell.is_second_half_posted
      ) {
        setEditSidebarSelectedItems((prevState) => {
          if (prevState.find((item) => item.cell.id === cell.id && item.halfPart === 'second_half')) {
            return prevState.filter((item) => !(item.cell.id === cell.id && item.halfPart === 'second_half'));
          }
          return [
            ...prevState,
            { cell, halfPart: 'second_half', shiftName: shiftsData[cell.shift_order], roleName: name, unitName: unit.unit.name },
          ];
        });
      } else if (
        editSidebarAction === 'off_due_to' &&
        cell.second_half_user?.user &&
        editSidebarSelectedUser.id === cell.second_half_user.user.id
      ) {
        setEditSidebarSelectedItems((prevState) => {
          if (prevState.find((item) => item.cell.id === cell.id && item.halfPart === 'second_half')) {
            return prevState.filter((item) => !(item.cell.id === cell.id && item.halfPart === 'second_half'));
          }
          return [
            ...prevState,
            { cell, halfPart: 'second_half', shiftName: shiftsData[cell.shift_order], roleName: name, unitName: unit.unit.name },
          ];
        });
      } else if (
        editSidebarAction === 'delete' &&
        cell.second_half_user?.user &&
        editSidebarSelectedUser.id === cell.second_half_user.user.id
      ) {
        setEditSidebarSelectedItems((prevState) => {
          if (prevState.find((item) => item.cell.id === cell.id && item.halfPart === 'second_half')) {
            return prevState.filter((item) => !(item.cell.id === cell.id && item.halfPart === 'second_half'));
          }
          return [
            ...prevState,
            { cell, halfPart: 'second_half', shiftName: shiftsData[cell.shift_order], roleName: name, unitName: unit.unit.name },
          ];
        });
      }

      return;
    }

    if (isPrint || !hasWritePermission || showRecurringShifts) {
      return;
    }

    // Pickup requests are handled from the shift's column header (AssignmentRequestsModal).
    if (isMobile) {
      return;
    }

    if (isSelectingPostShiftsActive && !cell.second_half_user && !cell.is_second_half_posted) {
      setSelectedShiftsToPost((prevState) => {
        if (prevState.find((item) => item.cell.id === cell.id)) {
          return prevState.filter((item) => item.cell.id !== cell.id);
        }
        return [
          ...prevState,
          { cell, halfPart: 'second_half', shiftName: shiftsData[cell.shift_order], roleName: name, unitName: unit.unit.name },
        ];
      });

      return;
    }

    if (isSelectingPostShiftsActive && (cell.second_half_user || cell.is_second_half_posted)) {
      return;
    }

    if (cell.second_half_user) {
      setCellAssignmentData({
        unit,
        cell,
        roleName: name,
        roleId: role.id,
        isPermanentRole: !!role.permanent_role,
        shiftLabel: shiftsData[cell.shift_order],
        available: cell.first_half_user ? 'second_half' : 'both_halves',
        assignment: cell.second_half_user,
        isPosted: false,
        halfPart: 'second_half',
        isPastPeriod,
        roleType: role.role,
        isPrint: isPrint,
      });
    } else {
      setCellAssignmentData({
        unit,
        cell,
        roleName: name,
        roleId: role.id,
        isPermanentRole: !!role.permanent_role,
        shiftLabel: shiftsData[cell.shift_order],
        available: 'second_half',
        isPosted: cell.is_second_half_posted,
        halfPart: 'second_half',
        isPastPeriod,
        roleType: role.role,
        isPrint: isPrint,
      });
    }
  };

  return (
    <div
      ref={cell.second_half_user ? drag : drop}
      className={cx('cell-assignment-item second-half', {
        empty: !cell.second_half_user,
        'posted-cell': cell.is_second_half_posted && !showRecurringShifts,
        isActive:
          (cellAssignmentData?.cell?.id === cell.id && cellAssignmentData?.halfPart === 'second_half') ||
          selectedShiftsToPost?.find((item) => item.cell.id === cell.id && item.halfPart === 'second_half'),
        isSelected: cell.second_half_user?.user?.id && editSidebarSelectedUser?.id === cell.second_half_user?.user?.id,
        isSelectedToAdd:
          editSidebarAction === 'add' &&
          editSidebarSelectedItems?.find((item) => item.cell.id === cell.id && item.halfPart === 'second_half'),
        isSelectedToOffDue:
          editSidebarAction === 'off_due_to' &&
          editSidebarSelectedItems?.find((item) => item.cell.id === cell.id && item.halfPart === 'second_half'),
        isSelectedToRemove:
          editSidebarAction === 'delete' &&
          editSidebarSelectedItems?.find((item) => item.cell.id === cell.id && item.halfPart === 'second_half'),
      })}
      style={
        cell.second_half_user && (!showRecurringShifts || cell.second_half_user.repeat !== 'no')
          ? {
              backgroundColor: cell.second_half_user.cell_color,
              color: cell.second_half_user.font_color,
            }
          : {}
      }
      data-tooltip-id={`user-tooltip-${cell.second_half_user?.id}`}
      onClick={handleCellClick}
    >
      {(!showRecurringShifts || (cell.second_half_user && cell.second_half_user.repeat !== 'no')) && (
        <>
          <div
            style={cell.is_second_half_posted ? {} : { position: 'relative', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
          >
            <span className={cx('cell-assignment-user-name', { 'no-hide': isPrint })}>
              {cell.second_half_user ? cell.second_half_user.user_name : cell.is_second_half_posted ? 'Posted' : ''}
            </span>
            <span
              className={cx('cell-assignment-user-note', { 'no-hide': isPrint })}
              style={cell.second_half_user?.off_reason ? { color: '#fff' } : {}}
            >
              {cell.second_half_user?.notes || cell.second_half_user?.off_reason || ''}
            </span>
          </div>
        </>
      )}
      <OvertimeMark assignment={cell.second_half_user} isPrint={isPrint} />
    </div>
  );
};

export default SecondHalfCell;
